/**
 * @module features/files/file-operations
 * @description File operations including view, download, and delete functionality
 */

import { getFileByArchivedFileObjectId, deleteFile as deleteFileAPI } from '../../core/api/file-api.js';
import { canViewInBrowser, getContentTypeName, getNotViewableReason, inferContentTypeFromFilename } from './file-utils.js';
import { showError, showSuccess } from '../../ui/components/error-display.js';
import { showErrorWithRecovery, ERROR_CONTEXTS } from '../../ui/components/error-recovery.js';
import { logger } from '../../utils/logger.js';
import { safeMonitoring as monitoring, ErrorCategory, ErrorSeverity } from '../../services/monitoring-safe.js';

// Debouncing state to prevent duplicate operations
const operationInProgress = new Map();

/**
 * View a file in the browser if supported, otherwise show informative message
 * @param {string} archivedFileObjectId - File ID to view
 * @param {string} entityType - Entity type (projects, opportunities, leads, contacts)
 */
export async function viewFile(archivedFileObjectId, entityType = 'projects') {
    // Prevent duplicate operations
    const operationKey = `view_${archivedFileObjectId}`;
    if (operationInProgress.get(operationKey)) {
        logger.debug(`⏳ View already in progress for file: ${archivedFileObjectId}`);
        return;
    }

    operationInProgress.set(operationKey, true);

    let fileData;
    try {
        logger.debug(`👁️ View requested for file ID: ${archivedFileObjectId}`);
        fileData = await getFileByArchivedFileObjectId(archivedFileObjectId, entityType);

        // If content type is generic/unknown, try to infer from filename
        let contentType = fileData.contentType;
        if (!contentType || contentType === 'application/octet-stream') {
            const inferredType = inferContentTypeFromFilename(fileData.name);
            if (inferredType) {
                contentType = inferredType;
                logger.debug(`   🔍 Inferred content type from filename: ${contentType}`);
            }
        }

        // Check if the file can actually be viewed in browser
        const isViewable = canViewInBrowser(contentType);
        logger.debug(`   📄 File: "${fileData.name}" | Type: ${contentType} | Viewable: ${isViewable}`);

        if (isViewable) {
            // Open viewable content (images, PDFs, etc.) in new tab
            logger.debug(`   ✅ Opening in browser`);

            // If we had to infer the content type (API returned null), and it's audio/video,
            // create an HTML page with media player to handle incorrect content-type headers
            if ((!fileData.contentType || fileData.contentType === 'application/octet-stream') &&
                (contentType.startsWith('audio/') || contentType.startsWith('video/'))) {
                logger.debug(`   🎵 Creating media player for ${contentType.startsWith('audio/') ? 'audio' : 'video'} file`);

                const mediaType = contentType.startsWith('audio/') ? 'audio' : 'video';
                const playerHtml = `
<!DOCTYPE html>
<html>
<head>
    <title>${fileData.name}</title>
    <meta charset="UTF-8">
    <style>
        body {
            margin: 0;
            padding: 20px;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: #1e1e1e;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        .container {
            text-align: center;
            max-width: 800px;
            width: 100%;
        }
        h1 {
            color: #fff;
            margin-bottom: 20px;
            font-size: 18px;
            word-break: break-word;
        }
        ${mediaType} {
            width: 100%;
            ${mediaType === 'video' ? 'max-height: 70vh;' : ''}
            background: #000;
            border-radius: 8px;
        }
        .error {
            color: #ff6b6b;
            margin-top: 20px;
        }
    </style>
</head>
<body>
    <div class="container">
        <h1>${fileData.name}</h1>
        <${mediaType} controls autoplay>
            <source src="${fileData.downloadUrl}" type="${contentType}">
            Your browser does not support the ${mediaType} element.
        </${mediaType}>
    </div>
</body>
</html>`;

                // eslint-disable-next-line no-undef
                const blob = new Blob([playerHtml], { type: 'text/html' });
                const blobUrl = window.URL.createObjectURL(blob);
                const newWindow = window.open(blobUrl, '_blank');

                // Clean up the blob URL after window loads
                setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000);

                if (!newWindow) {
                    logger.warn(`   ⚠️ Popup blocked, falling back to direct URL`);
                    window.open(fileData.downloadUrl, '_blank');
                }
            } else {
                // For images, PDFs, or files with proper content type, open directly
                window.open(fileData.downloadUrl, '_blank');
            }
        } else {
            // Not viewable - inform the user why
            logger.debug(`   ℹ️ File cannot be viewed in browser`);

            const typeName = getContentTypeName(contentType);
            const reason = getNotViewableReason(contentType);

            showError(
                `Cannot view this file in browser.\n\n` +
                `File type: ${typeName}\n` +
                `Reason: ${reason}\n\n` +
                `Please use the Download button to save the file and open it with the appropriate application.`
            );
        }
    } catch (error) {
        logger.error('Error viewing file:', error);

        // Track view file error with enhanced network detection
        if (monitoring.isInitialized()) {
            const isNetworkError = error instanceof TypeError &&
                (error.message.includes('Load failed') ||
                 error.message.includes('Failed to fetch') ||
                 error.message.includes('NetworkError'));

            monitoring.trackError(error, {
                category: ErrorCategory.DOWNLOAD,
                severity: ErrorSeverity.ERROR,
                operation: 'viewFile',
                entityType: entityType,
                fileId: archivedFileObjectId,
                fileName: fileData?.name,
                // Enhanced network error detection
                errorType: error.name,
                errorMessage: error.message,
                isNetworkError: isNetworkError,
                httpStatusCode: error.status || error.response?.status,
                possibleCause: isNetworkError ? 'client_network_issue' : 'server_or_code_error'
            });
        }

        // Use enhanced error recovery
        showErrorWithRecovery(error, {
            type: ERROR_CONTEXTS.FILE_OPERATION,
            fileName: fileData?.name,
            entityType,
            retryAction: () => viewFile(archivedFileObjectId, entityType)
        });
    } finally {
        // Clear operation lock after delay to prevent immediate retries
        const operationKey = `view_${archivedFileObjectId}`;
        setTimeout(() => operationInProgress.delete(operationKey), 1000);
    }
}

/**
 * Download a file to the user's device
 * @param {string} archivedFileObjectId - File ID to download
 * @param {string} fileName - Name to save the file as
 * @param {string} entityType - Entity type (projects, opportunities, leads, contacts)
 */
export async function downloadFile(archivedFileObjectId, fileName, entityType = 'projects') {
    // Prevent duplicate operations
    const operationKey = `download_${archivedFileObjectId}`;
    if (operationInProgress.get(operationKey)) {
        logger.debug(`⏳ Download already in progress for file: ${archivedFileObjectId}`);
        return;
    }

    operationInProgress.set(operationKey, true);

    let downloadUrl = null;
    let fetchStarted = false;

    try {
        const fileData = await getFileByArchivedFileObjectId(archivedFileObjectId, entityType);
        downloadUrl = fileData.downloadUrl;

        fetchStarted = true;
        const response = await fetch(downloadUrl);
        if (!response.ok) {
            throw new Error(`Failed to download: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
        logger.error('Error downloading file:', error);

        // Track download error with enhanced network detection
        if (monitoring.isInitialized()) {
            const isNetworkError = error instanceof TypeError &&
                (error.message.includes('Load failed') ||
                 error.message.includes('Failed to fetch') ||
                 error.message.includes('NetworkError'));

            monitoring.trackError(error, {
                category: ErrorCategory.DOWNLOAD,
                severity: ErrorSeverity.ERROR,
                operation: 'downloadFile',
                entityType: entityType,
                fileId: archivedFileObjectId,
                fileName: fileName,
                // Enhanced network error detection
                url: downloadUrl,
                errorType: error.name,
                errorMessage: error.message,
                isNetworkError: isNetworkError,
                fetchAttempted: fetchStarted,
                httpStatusCode: error.status || error.response?.status,
                possibleCause: isNetworkError ? 'client_network_issue' : 'server_or_code_error'
            });
        }

        // Use enhanced error recovery
        showErrorWithRecovery(error, {
            type: ERROR_CONTEXTS.FILE_OPERATION,
            fileName,
            entityType,
            retryAction: () => downloadFile(archivedFileObjectId, fileName, entityType)
        });
    } finally {
        // Clear operation lock after delay to prevent immediate retries
        const operationKey = `download_${archivedFileObjectId}`;
        setTimeout(() => operationInProgress.delete(operationKey), 1000);
    }
}

/**
 * Delete a file from the archive (requires authorization)
 * @param {string} archivedFileObjectId - File ID to delete
 * @param {string} fileName - File name for confirmation dialog
 * @param {Object} user - Firebase user object
 * @param {string} entityType - Entity type (projects, opportunities, leads, contacts)
 * @param {string|null} entityId - Entity ID the file belongs to
 * @param {HTMLElement} filesContainer - Container element for file list
 */
export async function deleteFile(archivedFileObjectId, fileName, user, entityType = 'projects', entityId = null, filesContainer) {
    // Confirm deletion with user
    const confirmMessage = `Are you sure you want to permanently delete "${fileName}"?\n\nThis action cannot be undone and will remove the file from both the archive and Insightly.`;

    // eslint-disable-next-line no-alert -- Intentional: critical operation requires explicit user confirmation
    if (!window.confirm(confirmMessage)) {
        return;
    }

    // Show loading state - find the file item and disable the delete button
    const fileElement = document.querySelector(`[data-file-id="${archivedFileObjectId}"]`);

    const deleteBtn = fileElement?.querySelector('.delete-btn');

    if (deleteBtn) {
        deleteBtn.disabled = true;
        deleteBtn.textContent = 'Deleting...';
    }

    try {
        // Call the DELETE API endpoint using the file-api module
        const result = await deleteFileAPI(archivedFileObjectId, entityType, entityId);

        // Show success message
        let successMessage = `File "${fileName}" has been successfully deleted.`;
        if (result.Errors && result.Errors.length > 0) {
            successMessage += `\n\nWarnings: ${result.Errors.join(', ')}`;
        }

        showSuccess(successMessage);

        // Remove the file element from the UI
        if (fileElement) {
            fileElement.remove();
        }

        // If no files remain, show the "no files" message
        const remainingFiles = document.querySelectorAll('.file-item');
        if (remainingFiles.length === 0 && filesContainer) {
            filesContainer.innerHTML = '<p>No files found in this archive.</p>';
        }

    } catch (error) {
        logger.error('Error deleting file:', error);

        // Track delete error
        if (monitoring.isInitialized()) {
            monitoring.trackError(error, {
                category: ErrorCategory.STORAGE,
                severity: ErrorSeverity.ERROR,
                operation: 'deleteFile',
                entityType: entityType,
                entityId: entityId,
                fileId: archivedFileObjectId,
                fileName: fileName
            });
        }

        // Reset the delete button state
        if (deleteBtn) {
            deleteBtn.disabled = false;
            deleteBtn.textContent = 'Delete';
        }

        // Show error message
        showError(`Failed to delete file: ${error.message}`);
    }
}

// Default export
export default {
    viewFile,
    downloadFile,
    deleteFile
};
